
import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Target, Calendar } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

type GoalSettingModalProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit?: (goalData: GoalData) => void;
};

export type GoalData = {
  title: string;
  category: string;
  dueDate: string;
};

export default function GoalSettingModal({ 
  isOpen, 
  onOpenChange,
  onSubmit 
}: GoalSettingModalProps) {
  const { toast } = useToast();
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("productivity");
  const [dueDate, setDueDate] = useState("");
  
  const handleSubmit = () => {
    if (!title) {
      toast({
        title: "Missing information",
        description: "Please enter a goal title",
        variant: "destructive",
      });
      return;
    }
    
    if (!dueDate) {
      toast({
        title: "Missing information",
        description: "Please select a due date",
        variant: "destructive",
      });
      return;
    }
    
    const goalData: GoalData = {
      title,
      category,
      dueDate,
    };
    
    if (onSubmit) {
      onSubmit(goalData);
    }
    
    // Reset form and close modal
    setTitle("");
    setCategory("productivity");
    setDueDate("");
    onOpenChange(false);
    
    toast({
      title: "Goal created",
      description: "Your weekly goal has been set!",
    });
  };
  
  // Calculate min date (today) for the date picker
  const today = new Date();
  const minDate = today.toISOString().split('T')[0];
  
  // Calculate next week's date for default
  const nextWeek = new Date(today);
  nextWeek.setDate(today.getDate() + 7);
  const defaultDueDate = nextWeek.toISOString().split('T')[0];
  
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md mx-4 w-[calc(100vw-2rem)]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Target className="h-5 w-5 text-primary" />
            <span>Set a Weekly Goal</span>
          </DialogTitle>
          <DialogDescription className="text-sm">
            Define a clear, achievable goal to focus on this week.
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="goal-title">Goal Title</Label>
            <Input
              id="goal-title"
              placeholder="What do you want to achieve?"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          
          <div className="grid gap-2">
            <Label htmlFor="goal-category">Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger id="goal-category">
                <SelectValue placeholder="Select a category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="productivity">Productivity</SelectItem>
                <SelectItem value="leadership">Leadership</SelectItem>
                <SelectItem value="learning">Learning</SelectItem>
                <SelectItem value="wellbeing">Wellbeing</SelectItem>
                <SelectItem value="teamwork">Teamwork</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="grid gap-2">
            <Label htmlFor="goal-due-date" className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              <span>Due Date</span>
            </Label>
            <Input
              id="goal-due-date"
              type="date"
              min={minDate}
              defaultValue={defaultDueDate}
              onChange={(e) => setDueDate(e.target.value)}
            />
          </div>
        </div>
        
        <DialogFooter className="flex-col space-y-2 sm:flex-row sm:space-y-0 sm:space-x-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="h-11 text-base order-2 sm:order-1">
            Cancel
          </Button>
          <Button onClick={handleSubmit} className="h-11 text-base order-1 sm:order-2">
            Set Goal
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
